/**
 * Circular-arc math for curved wall-sketch edges.
 *
 * An edge's curvature is stored as a single `bulge` number (DXF-style
 * magnitude: bulge = tan(θ/4), θ = included angle). Sign convention:
 * positive bulge bows the arc to the LEFT of the start→end direction
 * (the same side `leftSize` thickens toward). 0 / absent = straight.
 *
 * Dependency-free (plain {x, y} points, no THREE) so it stays trivially
 * unit-testable and liftable to potree-shared. All distances are meters,
 * all angles radians. Every consumer of curved edges routes through this
 * module — either the analytic primitives (hit-testing, snapping, opening
 * parametrization) or the tessellators (rendering, CSG footprints).
 */

export type Pt2 = { x: number; y: number };

/** Below this |bulge| an edge is treated as perfectly straight. */
export const BULGE_STRAIGHT_EPS = 1e-3;

/** Default chord-error tolerance for tessellation, meters (world space). */
export const ARC_CHORD_TOLERANCE = 0.005;

/** Hard cap on tessellation segments per edge. */
export const ARC_MAX_SEGMENTS = 96;

/** Maximum included angle allowed when authoring an arc (avoid near-full circles). */
export const ARC_MAX_INCLUDED_ANGLE = (340 / 180) * Math.PI;

export function edgeIsCurved(edge: { bulge?: number }): boolean {
  return Math.abs(edge.bulge ?? 0) >= BULGE_STRAIGHT_EPS;
}

/** Resolved circle of a curved edge. */
export interface ArcGeom {
  center: Pt2;
  radius: number;
  /** Angle of `start` from center (radians). */
  startAngle: number;
  /**
   * Signed sweep from start to end: angle(t) = startAngle + t·sweep, t ∈ [0, 1].
   * Positive bulge (bows left) sweeps clockwise, i.e. sweep = −4·atan(bulge).
   */
  sweep: number;
  /** Total arc length = radius · |sweep|. */
  length: number;
}

const TAU = Math.PI * 2;

function sub(a: Pt2, b: Pt2): Pt2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

function hypot(p: Pt2): number {
  return Math.hypot(p.x, p.y);
}

/** Unit left perpendicular of a direction (rotate +90°). */
function leftPerp(d: Pt2): Pt2 {
  return { x: -d.y, y: d.x };
}

/**
 * Resolve the circle of an arc edge. Returns null when the edge is straight
 * (|bulge| < eps) or the chord is degenerate — callers fall back to segment math.
 */
export function arcFromBulge(start: Pt2, end: Pt2, bulge: number): ArcGeom | null {
  if (Math.abs(bulge) < BULGE_STRAIGHT_EPS) return null;
  const chord = sub(end, start);
  const chordLen = hypot(chord);
  if (chordLen < 1e-9) return null;
  const dir = { x: chord.x / chordLen, y: chord.y / chordLen };
  const perp = leftPerp(dir);
  const sagitta = (chordLen / 2) * bulge;
  const radius = (chordLen / 4) * (Math.abs(bulge) + 1 / Math.abs(bulge));
  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  // Apex sits at mid + perp·sagitta; the center lies radius further along
  // −perp·sign(bulge) from the apex (minor arc: opposite side of the chord).
  const centerOffset = sagitta - Math.sign(bulge) * radius;
  const center = { x: mid.x + perp.x * centerOffset, y: mid.y + perp.y * centerOffset };
  const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
  const sweep = -4 * Math.atan(bulge);
  return { center, radius, startAngle, sweep, length: radius * Math.abs(sweep) };
}

/** Chord (straight) or arc length of an edge. */
export function edgeLength(start: Pt2, end: Pt2, bulge: number | undefined): number {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  return arc ? arc.length : hypot(sub(end, start));
}

/**
 * Point + unit tangent (in start→end travel direction) at arc-length
 * s ∈ [0, length]. Linear interpolation fallback for straight edges.
 */
export function pointAtArcLength(
  start: Pt2,
  end: Pt2,
  bulge: number | undefined,
  s: number,
): { point: Pt2; tangent: Pt2 } {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  if (!arc) {
    const chord = sub(end, start);
    const len = hypot(chord);
    const dir = len > 1e-9 ? { x: chord.x / len, y: chord.y / len } : { x: 1, y: 0 };
    const t = len > 1e-9 ? s / len : 0;
    return { point: { x: start.x + chord.x * t, y: start.y + chord.y * t }, tangent: dir };
  }
  const t = arc.length > 1e-9 ? s / arc.length : 0;
  const a = arc.startAngle + t * arc.sweep;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const point = { x: arc.center.x + arc.radius * cos, y: arc.center.y + arc.radius * sin };
  const rot = Math.sign(arc.sweep);
  return { point, tangent: { x: -sin * rot, y: cos * rot } };
}

/**
 * Closest point on the (possibly curved) edge. `s` is the arc-length
 * parameter of the foot point, `distance` the euclidean distance to `p`.
 */
export function closestPointOnEdge(
  p: Pt2,
  start: Pt2,
  end: Pt2,
  bulge: number | undefined,
): { point: Pt2; s: number; distance: number } {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  if (!arc) {
    const chord = sub(end, start);
    const lenSq = chord.x * chord.x + chord.y * chord.y;
    const t = lenSq > 1e-18 ? Math.min(1, Math.max(0, ((p.x - start.x) * chord.x + (p.y - start.y) * chord.y) / lenSq)) : 0;
    const point = { x: start.x + chord.x * t, y: start.y + chord.y * t };
    return { point, s: t * Math.sqrt(lenSq), distance: hypot(sub(p, point)) };
  }
  const v = sub(p, arc.center);
  const vLen = hypot(v);
  if (vLen > 1e-9) {
    const a = Math.atan2(v.y, v.x);
    // Signed angular travel from startAngle in the sweep direction, in [0, 2π).
    const travel = Math.sign(arc.sweep) >= 0 ? ((a - arc.startAngle) % TAU + TAU) % TAU : ((arc.startAngle - a) % TAU + TAU) % TAU;
    if (travel <= Math.abs(arc.sweep)) {
      const point = { x: arc.center.x + (v.x / vLen) * arc.radius, y: arc.center.y + (v.y / vLen) * arc.radius };
      return { point, s: arc.radius * travel, distance: Math.abs(vLen - arc.radius) };
    }
  }
  const dStart = hypot(sub(p, start));
  const dEnd = hypot(sub(p, end));
  return dStart <= dEnd
    ? { point: { ...start }, s: 0, distance: dStart }
    : { point: { ...end }, s: arc.length, distance: dEnd };
}

function segmentCountFor(sweepAbs: number, radius: number, tolerance: number): number {
  if (tolerance >= radius) return 1;
  const maxSegAngle = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - tolerance / radius)));
  if (!(maxSegAngle > 1e-9)) return ARC_MAX_SEGMENTS;
  return Math.max(1, Math.min(ARC_MAX_SEGMENTS, Math.ceil(sweepAbs / maxSegAngle)));
}

/**
 * Polyline including both endpoints; [start, end] for straight edges.
 * Segment count derives from the chord-error tolerance (world space).
 */
export function tessellateEdge(start: Pt2, end: Pt2, bulge: number | undefined, tolerance = ARC_CHORD_TOLERANCE): Pt2[] {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  if (!arc) return [{ ...start }, { ...end }];
  const n = segmentCountFor(Math.abs(arc.sweep), arc.radius, tolerance);
  const pts: Pt2[] = [{ ...start }];
  for (let i = 1; i < n; i++) {
    const a = arc.startAngle + (i / n) * arc.sweep;
    pts.push({ x: arc.center.x + arc.radius * Math.cos(a), y: arc.center.y + arc.radius * Math.sin(a) });
  }
  pts.push({ ...end });
  return pts;
}

/**
 * Offset polyline at signed perpendicular distance d (+d = left of travel).
 * For arcs this is the same circle with radius + d·sign(bulge); the radius is
 * clamped to a small positive value when the offset would cross the center
 * (wall thicker than the radius of curvature).
 */
export function tessellateOffset(
  start: Pt2,
  end: Pt2,
  bulge: number | undefined,
  d: number,
  tolerance = ARC_CHORD_TOLERANCE,
): Pt2[] {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  if (!arc) {
    const chord = sub(end, start);
    const len = hypot(chord);
    const dir = len > 1e-9 ? { x: chord.x / len, y: chord.y / len } : { x: 1, y: 0 };
    const p = leftPerp(dir);
    return [
      { x: start.x + p.x * d, y: start.y + p.y * d },
      { x: end.x + p.x * d, y: end.y + p.y * d },
    ];
  }
  const sign = Math.sign(bulge ?? 0);
  const offsetRadius = Math.max(1e-4, arc.radius + d * sign);
  // Sample with the density of the larger radius so bands share angle steps.
  const n = segmentCountFor(Math.abs(arc.sweep), Math.max(arc.radius, offsetRadius), tolerance);
  const pts: Pt2[] = [];
  for (let i = 0; i <= n; i++) {
    const a = arc.startAngle + (i / n) * arc.sweep;
    pts.push({ x: arc.center.x + offsetRadius * Math.cos(a), y: arc.center.y + offsetRadius * Math.sin(a) });
  }
  return pts;
}

/**
 * Both offset polylines of a wall band, sampled at the SAME angles so
 * left[i]/right[i] pair up into a quad strip (equal lengths guaranteed).
 * For straight edges each side is a 2-point segment.
 */
export function tessellateBandPair(
  start: Pt2,
  end: Pt2,
  bulge: number | undefined,
  leftSize: number,
  rightSize: number,
  tolerance = ARC_CHORD_TOLERANCE,
): { left: Pt2[]; right: Pt2[] } {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  if (!arc) {
    return {
      left: tessellateOffset(start, end, 0, leftSize, tolerance),
      right: tessellateOffset(start, end, 0, -rightSize, tolerance),
    };
  }
  const sign = Math.sign(bulge ?? 0);
  const leftRadius = Math.max(1e-4, arc.radius + leftSize * sign);
  const rightRadius = Math.max(1e-4, arc.radius - rightSize * sign);
  const n = segmentCountFor(Math.abs(arc.sweep), Math.max(arc.radius, leftRadius, rightRadius), tolerance);
  const left: Pt2[] = [];
  const right: Pt2[] = [];
  for (let i = 0; i <= n; i++) {
    const a = arc.startAngle + (i / n) * arc.sweep;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    left.push({ x: arc.center.x + leftRadius * cos, y: arc.center.y + leftRadius * sin });
    right.push({ x: arc.center.x + rightRadius * cos, y: arc.center.y + rightRadius * sin });
  }
  return { left, right };
}

/**
 * Closed wall-band ring for an edge: left offset polyline followed by the
 * reversed right offset polyline. Both sides share the same angular sampling.
 */
export function tessellateBand(
  start: Pt2,
  end: Pt2,
  bulge: number | undefined,
  leftSize: number,
  rightSize: number,
  tolerance = ARC_CHORD_TOLERANCE,
): Pt2[] {
  const { left, right } = tessellateBandPair(start, end, bulge, leftSize, rightSize, tolerance);
  right.reverse();
  return [...left, ...right];
}

/**
 * Split an arc at arc-length fraction t of its total length. Returns the
 * bulges [first, second]; both halves stay exactly on the original circle.
 */
export function splitBulge(bulge: number, t: number): [number, number] {
  const quarter = Math.atan(bulge); // θ/4, signed
  return [Math.tan(t * quarter), Math.tan((1 - t) * quarter)];
}

/**
 * Sub-span of an edge between arc lengths s0..s1 (clamped to [0, length]):
 * the span's endpoints plus the bulge that keeps it on the original circle.
 * For straight edges the bulge stays 0 and endpoints interpolate linearly.
 * Used to build opening cutouts that follow the wall's curve.
 */
export function subSpan(start: Pt2, end: Pt2, bulge: number | undefined, s0: number, s1: number): { start: Pt2; end: Pt2; bulge: number } {
  const total = edgeLength(start, end, bulge);
  const a = Math.max(0, Math.min(total, Math.min(s0, s1)));
  const b = Math.max(0, Math.min(total, Math.max(s0, s1)));
  const p0 = pointAtArcLength(start, end, bulge, a).point;
  const p1 = pointAtArcLength(start, end, bulge, b).point;
  const fraction = total > 1e-9 ? (b - a) / total : 0;
  return { start: p0, end: p1, bulge: Math.tan(fraction * Math.atan(bulge ?? 0)) };
}

/** Arc midpoint (apex) — the bow-handle anchor. Chord midpoint when straight. */
export function arcMidpoint(start: Pt2, end: Pt2, bulge: number | undefined): Pt2 {
  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const b = bulge ?? 0;
  if (Math.abs(b) < BULGE_STRAIGHT_EPS) return mid;
  const chord = sub(end, start);
  const len = hypot(chord);
  if (len < 1e-9) return mid;
  const p = leftPerp({ x: chord.x / len, y: chord.y / len });
  const sagitta = (len / 2) * b;
  return { x: mid.x + p.x * sagitta, y: mid.y + p.y * sagitta };
}

/** Unit tangent pointing OUT of the start node (chord direction when straight). */
export function outgoingTangentAtStart(start: Pt2, end: Pt2, bulge: number | undefined): Pt2 {
  return pointAtArcLength(start, end, bulge, 0).tangent;
}

/** Unit tangent pointing OUT of the end node (reverse chord direction when straight). */
export function outgoingTangentAtEnd(start: Pt2, end: Pt2, bulge: number | undefined): Pt2 {
  const len = edgeLength(start, end, bulge);
  const { tangent } = pointAtArcLength(start, end, bulge, len);
  return { x: -tangent.x, y: -tangent.y };
}

/** bulge = 2·sagitta / chord (both signed toward the left perpendicular). */
export function bulgeFromSagitta(chord: number, sagitta: number): number {
  return chord > 1e-9 ? (2 * sagitta) / chord : 0;
}

export function sagittaFromBulge(chord: number, bulge: number): number {
  return (chord / 2) * bulge;
}

/** Radius of the arc's circle; Infinity for straight edges. */
export function radiusFromBulge(chord: number, bulge: number): number {
  const b = Math.abs(bulge);
  if (b < BULGE_STRAIGHT_EPS) return Infinity;
  return (chord / 4) * (b + 1 / b);
}

/**
 * Minor-arc bulge for a target radius; `sign` picks the bow side (+1 = left).
 * A radius below chord/2 is clamped to the semicircle (|bulge| = 1).
 */
export function bulgeFromRadius(chord: number, radius: number, sign: 1 | -1): number {
  if (!(chord > 1e-9) || !isFinite(radius)) return 0;
  const half = chord / 2;
  if (radius <= half) return sign;
  const sagitta = radius - Math.sqrt(radius * radius - half * half);
  return sign * bulgeFromSagitta(chord, sagitta);
}

function angularTravel(arc: ArcGeom, pointAngle: number): number {
  return Math.sign(arc.sweep) >= 0
    ? ((pointAngle - arc.startAngle) % TAU + TAU) % TAU
    : ((arc.startAngle - pointAngle) % TAU + TAU) % TAU;
}

/**
 * Infinite line (origin + t·dir) ∩ edge — 0..2 hits within the edge's span.
 * Handles straight edges as a segment for convenience.
 */
export function lineArcIntersections(origin: Pt2, dir: Pt2, start: Pt2, end: Pt2, bulge: number | undefined): Pt2[] {
  const arc = arcFromBulge(start, end, bulge ?? 0);
  if (!arc) {
    // Line × segment.
    const seg = sub(end, start);
    const denom = dir.x * seg.y - dir.y * seg.x;
    if (Math.abs(denom) < 1e-12) return [];
    const dx = start.x - origin.x;
    const dy = start.y - origin.y;
    const u = (dy * dir.x - dx * dir.y) / -denom; // param on segment
    if (u < 0 || u > 1) return [];
    return [{ x: start.x + seg.x * u, y: start.y + seg.y * u }];
  }
  // Line × circle: |origin + t·dir − center|² = r².
  const ox = origin.x - arc.center.x;
  const oy = origin.y - arc.center.y;
  const a = dir.x * dir.x + dir.y * dir.y;
  const b = 2 * (ox * dir.x + oy * dir.y);
  const c = ox * ox + oy * oy - arc.radius * arc.radius;
  const disc = b * b - 4 * a * c;
  if (disc < 0 || a < 1e-18) return [];
  const sq = Math.sqrt(disc);
  const hits: Pt2[] = [];
  for (const t of disc === 0 ? [-b / (2 * a)] : [(-b - sq) / (2 * a), (-b + sq) / (2 * a)]) {
    const p = { x: origin.x + dir.x * t, y: origin.y + dir.y * t };
    const travel = angularTravel(arc, Math.atan2(p.y - arc.center.y, p.x - arc.center.x));
    if (travel <= Math.abs(arc.sweep)) hits.push(p);
  }
  return hits;
}

/**
 * Internal bulge (positive bows LEFT of A→B, clockwise sweep) → DXF group-42
 * bulge (positive = counter-clockwise sweep, bows RIGHT of A→B). The single
 * flip point for every DXF export path — if arcs bow the wrong way in a CAD
 * viewer, this is the line to fix.
 */
export function dxfBulgeFromInternal(bulge: number): number {
  return -bulge;
}

/** Circle a re-fit run may lie on (a curved wall's face circle). */
export interface ArcCandidate {
  center: Pt2;
  radius: number;
}

export interface RefitOptions {
  /** On-circle tolerance for run membership. */
  onCircleTol?: number;
  /** Looser tolerance for extending a run by one vertex at each end. */
  endExtendTol?: number;
}

/**
 * On-circle tolerance: tessellation samples lie exactly on the circle, only
 * perturbed by the SDF partition's 0.1 mm snap + the CSG's integer
 * quantization. Well below ARC_CHORD_TOLERANCE so mid-chord points never match.
 */
export const REFIT_ON_CIRCLE_TOL = 0.001;
/**
 * Run-end extension tolerance: cut/miter endpoints sit on tessellation chords,
 * up to ARC_CHORD_TOLERANCE inside the true circle.
 */
export const REFIT_END_EXTEND_TOL = 0.006;

function wrapToPi(a: number): number {
  return ((((a + Math.PI) % TAU) + TAU) % TAU) - Math.PI;
}

interface RefitRun {
  /** Ring index of the run's first vertex. */
  start: number;
  /** Number of consecutive ring vertices in the run. */
  count: number;
  /** Signed CCW angular travel from first to last vertex. */
  travel: number;
}

/**
 * Re-fit circular arcs onto a closed ring (no duplicated closing vertex) of
 * tessellated points, given the candidate circles the arcs can lie on (known
 * from the wall edges — this is classification, not blind curve fitting).
 * Maximal runs of consecutive on-circle vertices collapse to their two
 * endpoints plus an INTERNAL-convention bulge; everything else passes through
 * unchanged, so a rejected run is never worse than the tessellation.
 * `bulges[i]` describes the segment points[i] → points[(i+1) % points.length].
 */
export function refitArcsToRing(ring: Pt2[], candidates: ArcCandidate[], opts?: RefitOptions): { points: Pt2[]; bulges: number[] } {
  const onCircleTol = opts?.onCircleTol ?? REFIT_ON_CIRCLE_TOL;
  const endExtendTol = opts?.endExtendTol ?? REFIT_END_EXTEND_TOL;

  let pts = ring;
  if (pts.length >= 2 && hypot(sub(pts[pts.length - 1], pts[0])) < 1e-9) pts = pts.slice(0, -1);
  const n = pts.length;
  const identity = () => ({ points: pts.map((p) => ({ ...p })), bulges: new Array<number>(n).fill(0) });
  if (n < 3 || candidates.length === 0) return identity();

  // Dedupe candidates — coincident face circles of adjacent edges merge so a
  // single arc can span an edge boundary.
  const cands: ArcCandidate[] = [];
  const seenCands = new Set<string>();
  for (const c of candidates) {
    const key = `${Math.round(c.center.x * 1e4)},${Math.round(c.center.y * 1e4)},${Math.round(c.radius * 1e4)}`;
    if (!seenCands.has(key)) {
      seenCands.add(key);
      cands.push(c);
    }
  }

  const angleAt = (cand: ArcCandidate, i: number) => Math.atan2(pts[i % n].y - cand.center.y, pts[i % n].x - cand.center.x);
  // Signed CCW travel across `count` run vertices starting at ring index
  // `start`. Null when any step is non-monotonic, ~zero, or ambiguous (≥ π/2)
  // — e.g. a straight wall grazing the circle at several vertices.
  const travelOf = (cand: ArcCandidate, start: number, count: number): number | null => {
    let total = 0;
    let sign = 0;
    let prev = angleAt(cand, start);
    for (let j = 1; j < count; j++) {
      const a = angleAt(cand, start + j);
      const d = wrapToPi(a - prev);
      prev = a;
      if (Math.abs(d) < 1e-6 || Math.abs(d) >= Math.PI / 2) return null;
      const s = Math.sign(d);
      if (sign === 0) sign = s;
      else if (s !== sign) return null;
      total += d;
    }
    return total;
  };

  const runs: RefitRun[] = [];

  for (const cand of cands) {
    const err: number[] = new Array(n);
    let tightCount = 0;
    for (let i = 0; i < n; i++) {
      err[i] = Math.abs(Math.hypot(pts[i].x - cand.center.x, pts[i].y - cand.center.y) - cand.radius);
      if (err[i] < onCircleTol) tightCount++;
    }
    if (tightCount < 3) continue;

    if (tightCount === n) {
      // The whole ring lies on one circle (a freestanding circular wall
      // face). Emit two ~half-circle arcs — LWPOLYLINE needs ≥ 2 vertices,
      // and each half stays well under ARC_MAX_INCLUDED_ANGLE.
      const closedTravel = travelOf(cand, 0, n + 1); // includes the wrap segment
      if (closedTravel !== null && Math.abs(Math.abs(closedTravel) - TAU) < 0.1) {
        // Split at the vertex closest to half the travel.
        let acc = 0;
        let split = 1;
        let best = Infinity;
        let prev = angleAt(cand, 0);
        for (let i = 1; i < n; i++) {
          const a = angleAt(cand, i);
          acc += wrapToPi(a - prev);
          prev = a;
          const d = Math.abs(Math.abs(acc) - Math.abs(closedTravel) / 2);
          if (d < best) {
            best = d;
            split = i;
          }
        }
        const t1 = travelOf(cand, 0, split + 1);
        if (t1 !== null) {
          return {
            points: [{ ...pts[0] }, { ...pts[split] }],
            bulges: [-Math.tan(t1 / 4), -Math.tan((closedTravel - t1) / 4)],
          };
        }
      }
      continue; // all-tight but not one clean loop — leave as chords
    }

    // Scan from a non-tight vertex so wraparound runs stay contiguous.
    let i0 = 0;
    while (err[i0] < onCircleTol) i0++;
    let k = 1;
    while (k < n) {
      if (err[(i0 + k) % n] >= onCircleTol) {
        k++;
        continue;
      }
      let len = 1;
      while (k + len < n && err[(i0 + k + len) % n] < onCircleTol) len++;
      let start = (i0 + k) % n;
      let count = len;
      k += len;
      // Extend one vertex at each end with the loose tolerance — cut/miter
      // endpoints sit slightly inside the circle, and the emitted bulge arc
      // passes exactly through them so no gap opens.
      const before = (start - 1 + n) % n;
      if (count < n && err[before] < endExtendTol) {
        start = before;
        count++;
      }
      if (count < n && err[(start + count) % n] < endExtendTol) count++;

      if (count < 3) continue;
      const travel = travelOf(cand, start, count);
      if (travel === null) continue;
      if (Math.abs(travel) > ARC_MAX_INCLUDED_ANGLE) {
        // Too much sweep for one arc segment: split at the median vertex —
        // both halves stay exactly on the circle.
        const mid = Math.floor(count / 2);
        const t1 = travelOf(cand, start, mid + 1);
        const t2 = travelOf(cand, (start + mid) % n, count - mid);
        if (t1 !== null && mid + 1 >= 3) runs.push({ start, count: mid + 1, travel: t1 });
        if (t2 !== null && count - mid >= 3) runs.push({ start: (start + mid) % n, count: count - mid, travel: t2 });
        continue;
      }
      runs.push({ start, count, travel });
    }
  }

  if (runs.length === 0) return identity();

  // Greedy conflict resolution on covered ring segments (segment i is
  // pts[i] → pts[i+1]); longest runs win. Shared endpoint vertices are fine.
  runs.sort((a, b) => b.count - a.count);
  const segTaken = new Array<boolean>(n).fill(false);
  const accepted: RefitRun[] = [];
  for (const run of runs) {
    let free = true;
    for (let j = 0; j < run.count - 1; j++) {
      if (segTaken[(run.start + j) % n]) {
        free = false;
        break;
      }
    }
    if (!free) continue;
    for (let j = 0; j < run.count - 1; j++) segTaken[(run.start + j) % n] = true;
    accepted.push(run);
  }

  // Emit: interior run vertices drop; the run's bulge rides its first vertex.
  const drop = new Array<boolean>(n).fill(false);
  const bulgeAt = new Array<number>(n).fill(0);
  for (const run of accepted) {
    for (let j = 1; j < run.count - 1; j++) drop[(run.start + j) % n] = true;
    bulgeAt[run.start] = -Math.tan(run.travel / 4);
  }
  const points: Pt2[] = [];
  const bulges: number[] = [];
  for (let i = 0; i < n; i++) {
    if (drop[i]) continue;
    points.push({ ...pts[i] });
    bulges.push(bulgeAt[i]);
  }
  return { points, bulges };
}

/**
 * Finite segment a→b ∩ edge. Returns hit points with the edge's arc-length
 * parameter `s`, ordered along the segment.
 */
export function segmentArcIntersections(
  a: Pt2,
  b: Pt2,
  start: Pt2,
  end: Pt2,
  bulge: number | undefined,
): Array<{ point: Pt2; s: number }> {
  const seg = sub(b, a);
  const segLen = hypot(seg);
  if (segLen < 1e-9) return [];
  const dir = { x: seg.x / segLen, y: seg.y / segLen };
  const arc = arcFromBulge(start, end, bulge ?? 0);
  const hits = lineArcIntersections(a, dir, start, end, bulge);
  const results: Array<{ point: Pt2; s: number; t: number }> = [];
  for (const p of hits) {
    const t = (p.x - a.x) * dir.x + (p.y - a.y) * dir.y;
    if (t < -1e-9 || t > segLen + 1e-9) continue;
    let s: number;
    if (arc) {
      s = arc.radius * angularTravel(arc, Math.atan2(p.y - arc.center.y, p.x - arc.center.x));
    } else {
      s = hypot(sub(p, start));
    }
    results.push({ point: p, s, t });
  }
  results.sort((u, v) => u.t - v.t);
  return results.map(({ point, s }) => ({ point, s }));
}
